type QueryValue = string | number | boolean | null | undefined | Record<string, unknown>;

export const queryKeys = {
  farms: {
    all: (userId?: string) => ['farms', 'all', userId ?? 'anonymous'] as const,
    list: (search = '') => ['farms', 'list', search] as const,
    single: (id?: string) => ['farm', 'single', id ?? 'unknown'] as const,
  },
  licenseMode: (farmId?: string) => ['license-mode', farmId ?? 'unknown'] as const,
  licenseSettings: () => ['license-settings'] as const,
  areas: {
    all: (farmId?: string) => ['areas', farmId ?? 'unknown'] as const,
    list: (farmId?: string, search = '') => ['areas', 'list', farmId ?? 'unknown', search] as const,
    single: (id?: string) => ['area', 'single', id ?? 'unknown'] as const,
    eligible: (farmId?: string, context = 'default') => ['areas', 'eligible', farmId ?? 'unknown', context] as const,
  },
  paddocks: {
    byArea: (farmId?: string, areaId?: string) => ['paddocks', farmId ?? 'unknown', areaId ?? 'unknown'] as const,
  },
  pasture: {
    overview: (farmId?: string, areaId?: string) => ['pasture-overview', farmId ?? 'unknown', areaId ?? 'unknown'] as const,
    areaLock: (farmId?: string, areaId?: string) => ['pasture-area-lock', farmId ?? 'unknown', areaId ?? 'unknown'] as const,
    movements: (farmId?: string, areaId?: string) => ['pasture-movements', farmId ?? 'unknown', areaId ?? 'unknown'] as const,
    reviews: (farmId?: string, areaId?: string) => ['pasture-reviews', farmId ?? 'unknown', areaId ?? 'unknown'] as const,
  },
  operacao: {
    root: (farmId?: string) => ['operacao', farmId ?? 'unknown'] as const,
    nutrition: (farmId?: string, date?: string) => ['operacao', 'nutrition', farmId ?? 'unknown', date ?? 'today'] as const,
    nutritionCreateSuggestion: (farmId?: string) => ['operacao', 'nutrition-create-suggestion', farmId ?? 'unknown'] as const,
    pasture: (farmId?: string) => ['operacao', 'pasture', farmId ?? 'unknown'] as const,
    configuration: (farmId?: string) => ['operacao', 'configuration', farmId ?? 'unknown'] as const,
  },
  // A fazenda vem logo depois do domínio, antes do recurso: é isso que faz
  // `root(farm)` ser prefixo real das demais chaves. O React Query casa
  // invalidação por prefixo, então com o recurso na frente do farmId nenhuma
  // lista era invalidada e a tela só atualizava com F5.
  automation: {
    root: (farmId?: string) => ['automation', farmId ?? 'unknown'] as const,
    wagons: (farmId?: string) => ['automation', farmId ?? 'unknown', 'wagons'] as const,
    areas: (farmId?: string) => ['automation', farmId ?? 'unknown', 'areas'] as const,
    overview: (farmId?: string, date?: string) =>
      ['automation', farmId ?? 'unknown', 'overview', date ?? 'today'] as const,
    audit: (farmId?: string, date?: string, wagonId?: string, endDate?: string) =>
      [
        'automation',
        farmId ?? 'unknown',
        'audit',
        date ?? 'today',
        wagonId ?? 'all',
        endDate ?? date ?? 'today',
      ] as const,
    // A ordem de execução é da fórmula e vale para todos os vagões da fazenda.
    formulaExecution: (farmId?: string, dietId?: string) =>
      ['automation', farmId ?? 'unknown', 'formula-execution', dietId ?? 'none'] as const,
    discards: (farmId?: string) =>
      ['automation', farmId ?? 'unknown', 'discards'] as const,
    deviceBindings: (farmId?: string) =>
      ['automation', farmId ?? 'unknown', 'device-bindings'] as const,
    // Pendências de campo: o que prende a finalização e o que espera um líder.
    dayBarrier: (farmId?: string, date?: string) =>
      ['automation', farmId ?? 'unknown', 'day-barrier', date ?? 'today'] as const,
    pendingLinks: (farmId?: string) =>
      ['automation', farmId ?? 'unknown', 'pending-links'] as const,
    blockedFacts: (farmId?: string) =>
      ['automation', farmId ?? 'unknown', 'blocked-facts'] as const,
    // Perfil é do equipamento, não da fazenda: a chave não leva farmId.
    deviceProfiles: () => ['automation', 'device-profiles'] as const,
  },
  sanitary: {
    summary: (farmId?: string) => ['sanitary', 'summary', farmId ?? 'unknown'] as const,
    overview: (farmId?: string, date?: string) => ['sanitary', 'overview', farmId ?? 'unknown', date ?? 'today'] as const,
    withdrawalAnimals: (farmId?: string) => ['sanitary', 'withdrawal-animals', farmId ?? 'unknown'] as const,
    occurrenceAnimalIds: (farmId?: string, status = 'open') => ['sanitary', 'occurrence-animal-ids', farmId ?? 'unknown', status] as const,
    agenda: (farmId?: string, date?: string) => ['sanitary', 'agenda', farmId ?? 'unknown', date ?? 'today'] as const,
    products: (farmId?: string) => ['sanitary', 'products', farmId ?? 'unknown'] as const,
    stock: (farmId?: string) => ['sanitary', 'stock', farmId ?? 'unknown'] as const,
    protocols: (farmId?: string) => ['sanitary', 'protocols', farmId ?? 'unknown'] as const,
    events: (farmId?: string) => ['sanitary', 'events', farmId ?? 'unknown'] as const,
    animals: (farmId?: string) => ['sanitary', 'animals', farmId ?? 'unknown'] as const,
    history: (farmId?: string, filters?: Record<string, unknown>) => ['sanitary', 'history', farmId ?? 'unknown', filters ?? {}] as const,
    occurrences: (farmId?: string, filters?: Record<string, unknown>) => ['sanitary', 'occurrences', farmId ?? 'unknown', filters ?? {}] as const,
    deaths: (farmId?: string, filters?: Record<string, unknown>) => ['sanitary', 'deaths', farmId ?? 'unknown', filters ?? {}] as const,
    scheduledHandlings: (farmId?: string, status = 'pending') => ['sanitary', 'scheduled-handlings', farmId ?? 'unknown', status] as const,
  },
  programs: {
    all: (farmId?: string) => ['programs', farmId ?? 'unknown'] as const,
    list: (farmId?: string, search = '') => ['programs', 'list', farmId ?? 'unknown', search] as const,
    single: (id?: string) => ['program', 'single', id ?? 'unknown'] as const,
  },
  diets: {
    all: (farmId?: string) => ['diets', farmId ?? 'unknown'] as const,
    list: (farmId?: string, search = '') => ['diets', 'list', farmId ?? 'unknown', search] as const,
    single: (id?: string) => ['diet', 'single', id ?? 'unknown'] as const,
  },
  items: {
    all: (farmId?: string) => ['items', farmId ?? 'unknown'] as const,
    list: (farmId?: string, search = '') => ['items', 'list', farmId ?? 'unknown', search] as const,
    single: (id?: string) => ['item', 'single', id ?? 'unknown'] as const,
    purchasePresentations: (farmId?: string, itemId?: string) =>
      ['items', 'purchase-presentations', farmId ?? 'unknown', itemId ?? 'unknown'] as const,
  },
  lots: {
    all: (farmId?: string) => ['lots', farmId ?? 'unknown'] as const,
    list: (farmId?: string, search = '') => ['lots', 'list', farmId ?? 'unknown', search] as const,
    single: (id?: string) => ['lot', 'single', id ?? 'unknown'] as const,
    movements: (id?: string) => ['lot', 'movements', id ?? 'unknown'] as const,
    areaProgramState: (farmId?: string, areaId?: string) => ['lot', 'area-program-state', farmId ?? 'unknown', areaId ?? 'unknown'] as const,
  },
  animals: {
    list: (farmId?: string, search = '') => ['animals', 'list', farmId ?? 'unknown', search] as const,
    single: (id?: string) => ['animal', 'single', id ?? 'unknown'] as const,
  },
  // A fazenda vem logo depois do domínio para `root(farm)` ser prefixo real
  // das demais chaves (invalidar o aviso de um animal atualiza também a lista).
  animalNotices: {
    root: (farmId?: string) => ['animal-notices', farmId ?? 'unknown'] as const,
    summary: (farmId?: string) => ['animal-notices', farmId ?? 'unknown', 'summary'] as const,
    byAnimal: (farmId?: string, animalId?: string, activeOnly = false) =>
      ['animal-notices', farmId ?? 'unknown', 'animal', animalId ?? 'unknown', activeOnly ? 'active' : 'all'] as const,
  },
  nutrition: {
    root: (farmId?: string) => ['nutrition', farmId ?? 'unknown'] as const,
    list: (farmId?: string, ...params: QueryValue[]) => ['nutrition', 'list', farmId ?? 'unknown', ...params] as const,
    single: (farmId?: string, date?: string) => ['nutrition', 'single', farmId ?? 'unknown', date ?? 'unknown'] as const,
    complete: (farmId?: string) => ['nutrition', 'complete', farmId ?? 'unknown'] as const,
    cancellationEligibility: (farmId?: string) => ['nutrition', 'cancellation-eligibility', farmId ?? 'unknown'] as const,
    finalizationJob: (farmId?: string, jobId?: string) => ['nutrition', 'finalization-job', farmId ?? 'unknown', jobId ?? 'unknown'] as const,
  },
  production: {
    list: (farmId?: string, page?: number, pageSize?: number, search = '') => ['production', 'list', farmId ?? 'unknown', page ?? 1, pageSize ?? 10, search] as const,
    preview: (farmId?: string, payload?: Record<string, unknown>) => ['production', 'preview', farmId ?? 'unknown', payload ?? {}] as const,
    detail: (farmId?: string, id?: string) => ['production', 'single', farmId ?? 'unknown', id ?? 'unknown'] as const,
  },
  inventory: {
    list: (farmId?: string, page?: number, pageSize?: number, search = '') => ['inventory', 'list', farmId ?? 'unknown', pageSize ?? 10, page ?? 1, search] as const,
    item: (farmId?: string, itemId?: string, search = '') => ['inventory', 'item', farmId ?? 'unknown', itemId ?? 'unknown', search] as const,
  },
  finances: {
    list: (farmId?: string, ...params: QueryValue[]) => ['finances', 'list', farmId ?? 'unknown', ...params] as const,
    detail: (id?: string) => ['finance', 'detail', id ?? 'unknown'] as const,
  },
  transfers: {
    list: (farmId?: string, ...params: QueryValue[]) => ['transfers', 'list', farmId ?? 'unknown', ...params] as const,
    incoming: (farmId?: string) => ['transfers', 'incoming', farmId ?? 'unknown'] as const,
    detail: (farmId?: string, id?: string) => ['transfers', 'detail', farmId ?? 'unknown', id ?? 'unknown'] as const,
    byAnimal: (farmId?: string, animalId?: string) => ['transfers', 'by-animal', farmId ?? 'unknown', animalId ?? 'unknown'] as const,
  },
  buyers: {
    all: (farmId?: string) => ['buyers', farmId ?? 'unknown'] as const,
    list: (farmId?: string, search = '') => ['buyers', 'list', farmId ?? 'unknown', search] as const,
    single: (id?: string) => ['buyer', 'single', id ?? 'unknown'] as const,
  },
  suppliers: {
    all: (farmId?: string) => ['suppliers', farmId ?? 'unknown'] as const,
    list: (farmId?: string, search = '') => ['suppliers', 'list', farmId ?? 'unknown', search] as const,
    single: (id?: string) => ['supplier', 'single', id ?? 'unknown'] as const,
  },
  // Financeiro gerencial: `root` é prefixo de todas as chaves do módulo.
  financial: {
    root: () => ['financial'] as const,
    accounts: (includeInactive: boolean) => ['financial', 'accounts', includeInactive] as const,
    categories: (includeInactive: boolean) => ['financial', 'categories', includeInactive] as const,
    costCenters: (farmId: string, includeInactive: boolean) =>
      ['financial', 'cost-centers', farmId, includeInactive] as const,
    documents: (farmId: string, ...params: QueryValue[]) => ['financial', 'documents', farmId, ...params] as const,
    document: (farmId: string, id: string) => ['financial', 'document', farmId, id] as const,
    counterparties: (farmId: string, kind: 'supplier' | 'buyer') =>
      ['financial', 'counterparties', farmId, kind] as const,
    cashAccounts: (farmId: string) => ['financial', 'cash-accounts', farmId] as const,
    stockPosition: (farmId: string, asOf: string) => ['financial', 'stock-position', farmId, asOf] as const,
    costing: (farmId: string, ...params: QueryValue[]) => ['financial', 'costing', farmId, ...params] as const,
    costingSettings: (farmId: string) => ['financial', 'costing-settings', farmId] as const,
    fuel: (farmId: string, month: string) => ['financial', 'fuel', farmId, month] as const,
    results: (scope: string, view: string, from: string, to: string) =>
      ['financial', 'results', scope, view, from, to] as const,
    stockEffects: (farmId: string, from: string, to: string) =>
      ['financial', 'stock-effects', farmId, from, to] as const,
    stockEntries: (farmId: string, search: string, onlyUnlinked: boolean) =>
      ['financial', 'stock-entries', farmId, search, onlyUnlinked] as const,
    sources: (farmId: string, kind: 'sales' | 'contracts', ...params: QueryValue[]) =>
      ['financial', 'sources', farmId, kind, ...params] as const,
    overview: (farmId: string, from: string, to: string) => ['financial', 'overview', farmId, from, to] as const,
    cashStatement: (accountId: string, from: string, to: string) =>
      ['financial', 'cash-statement', accountId, from, to] as const,
    cashForecast: (farmId: string, from: string, to: string) =>
      ['financial', 'cash-forecast', farmId, from, to] as const,
    purchases: (farmId: string, ...params: QueryValue[]) => ['financial', 'purchases', farmId, ...params] as const,
    purchase: (farmId: string, id: string) => ['financial', 'purchase', farmId, id] as const,
    /** Anexos de compra, conta ou baixa, pela rota do dono (E4). */
    attachments: (ownerPath: string) => ['financial', 'attachments', ownerPath] as const,
    fiscalInbox: (farmId: string, ...params: QueryValue[]) => ['financial', 'fiscal-inbox', farmId, ...params] as const,
    contractPayables: (farmId: string, contractId: string) =>
      ['financial', 'contract-payables', farmId, contractId] as const,
    /** Projetos (E5B): lista com filtros e ficha com a página das contas. */
    projects: (farmId: string, ...params: QueryValue[]) => ['financial', 'projects', farmId, ...params] as const,
    project: (farmId: string, id: string, page: number) => ['financial', 'project', farmId, id, page] as const,
  },
  users: {
    list: (farmId?: string, ...params: QueryValue[]) => ['users', 'list', farmId ?? 'unknown', ...params] as const,
    single: (id?: string) => ['user', 'single', id ?? 'unknown'] as const,
  },
  reports: {
    hubCatalog: (farmId?: string) => ['reports-hub', 'catalog', farmId ?? 'unknown'] as const,
    hubOptions: (farmId?: string, reportKey?: string) => ['reports-hub', 'options', farmId ?? 'unknown', reportKey ?? 'unknown'] as const,
    hubRun: (farmId?: string, reportKey?: string, filters?: Record<string, unknown>) => ['reports-hub', 'run', farmId ?? 'unknown', reportKey ?? 'unknown', filters ?? {}] as const,
    legacy: (farmId?: string, ...params: QueryValue[]) => ['report', farmId ?? 'unknown', ...params] as const,
  },
  dashboard: {
    summary: (farmId?: string) => ['dashboard', 'summary', 'complete', farmId ?? 'unknown'] as const,
    summaryCards: (farmId?: string) => ['dashboard', 'summary-cards', farmId ?? 'unknown'] as const,
    financial: (farmId?: string, ...params: QueryValue[]) => ['dashboard', 'financial', farmId ?? 'unknown', ...params] as const,
  },
} as const;
